import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarPlus, Check, Download, Phone } from "lucide-react";
import { HOST } from "../lib/content";
import { calendarLinks } from "../lib/slots";

export default function ThankYouPage() {
  const slot = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("dfl_slot"));
    } catch {
      return null;
    }
  }, []);
  const lead = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("dfl_lead"));
    } catch {
      return null;
    }
  }, []);

  const links = slot ? calendarLinks(slot.start, slot.label) : null;

  const downloadIcs = () => {
    const blob = new Blob([links.ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "debt-free-life-strategy-call.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-[#0E2A54] text-[#D9E4F5] min-h-screen grid-bg" data-testid="thank-you-page">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#091E42]/60 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to="/" data-testid="thankyou-back-home-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-[#FFFFFF] transition-colors duration-200">
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO HOME
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 pt-16 md:pt-24 pb-24 text-center">
        <div className="w-20 h-20 mx-auto rounded-full border-2 border-[#FFFFFF] flex items-center justify-center mb-8">
          <Check className="w-9 h-9 text-[#FFFFFF]" />
        </div>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-4">CALL CONFIRMED</p>
        <h1 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(32px,5vw,56px)] text-[#D9E4F5] mb-5">
          {lead?.firstName ? `YOU'RE ALL SET, ${lead.firstName}.` : "YOU'RE ALL SET."}
        </h1>
        {slot ? (
          <p className="font-mono text-[13px] tracking-[0.12em] text-[#FFFFFF] mb-6" data-testid="thankyou-slot-label">
            <Phone className="w-4 h-4 inline mr-2" />
            {slot.label} · 45 MIN · PHONE CALL
          </p>
        ) : (
          <p className="text-[14px] text-white/50 mb-6">Your call request has been received.</p>
        )}
        <p className="text-[15px] text-white/50 leading-[1.8] max-w-md mx-auto mb-10">
          A confirmation is on its way to your inbox{lead?.email ? ` (${lead.email})` : ""}. Najiyyah will call you at the number you provided. Have your rough mortgage balance and monthly debt payments handy — she'll handle the math.
        </p>

        {links && (
          <div className="border border-white/12 bg-[#091E42]/45 backdrop-blur-xl p-7 mb-10" data-testid="calendar-options-card">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#FFFFFF] mb-5">ADD A REMINDER TO YOUR CALENDAR</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={links.google}
                target="_blank"
                rel="noreferrer"
                data-testid="add-google-calendar-btn"
                data-cta="true"
                className="inline-flex items-center justify-center gap-2 bg-[#FFFFFF] text-[#0E2A54] font-bold text-sm tracking-wide px-6 py-3.5 hover:bg-[#DCE8FF] transition-colors duration-200"
              >
                <CalendarPlus className="w-4 h-4" /> GOOGLE CALENDAR
              </a>
              <button
                onClick={downloadIcs}
                data-testid="download-ics-btn"
                data-cta="true"
                className="inline-flex items-center justify-center gap-2 border border-[#FFFFFF] text-[#FFFFFF] font-bold text-sm tracking-wide px-6 py-3.5 hover:bg-[#FFFFFF]/10 transition-colors duration-200"
              >
                <Download className="w-4 h-4" /> APPLE / OUTLOOK (.ICS)
              </button>
            </div>
            <p className="font-mono text-[10px] text-white/25 mt-4">Includes a 30-minute reminder before your call</p>
          </div>
        )}

        <div className="text-left border-t border-white/10 pt-8">
          <p className="font-mono text-[10px] tracking-[0.3em] text-white/35 mb-4">WHAT TO HAVE READY</p>
          <ul className="space-y-3">
            {[
              "Your rough mortgage balance and monthly payment",
              "Monthly payments on credit cards, student loans, or car notes",
              "Your questions — nothing is off the table",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-[14px] text-white/60">
                <Check className="w-4 h-4 text-[#FFFFFF] shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-white/30 leading-relaxed mt-8">
            This is an educational consultation with {HOST.name}, {HOST.company}. It is not an insurance application or financial commitment. Qualification required; results vary.
          </p>
        </div>
      </div>
    </div>
  );
}
